﻿import { useEffect, useState } from 'react';
import { Row, Col, Form, Button, Spinner as BsSpinner } from 'react-bootstrap';
import { FiSave } from 'react-icons/fi';
import { api } from '@/services/api';
import { config } from '@/config/env';
import { useToast } from '@/components/common/Toast';
import SidePanel from '@/components/common/SidePanel';
import type { Prospect } from '@/hooks/useProspects';

interface ProspectDetail {
  id: string;
  codcliente: string;
  ragsociale: string;
  codazienda: string;
  indirizzo: string;
  cap: string;
  localita: string;
  regioncountry: string;
  stato: string;
  telefono: string;
  email: string;
  [key: string]: unknown;
}

interface Props {
  prospect: Prospect | null;
  show: boolean;
  onHide: () => void;
  onSaved?: (updated: { codcliente: string; ragsociale: string; indirizzo: string; localita: string; prov?: string; telefono: string; email: string }) => void;
}

export default function ProspectDetailPanel({ prospect, show, onHide, onSaved }: Props) {
  const { showToast } = useToast();
  const [detail, setDetail] = useState<ProspectDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codiceEffettivo, setCodiceEffettivo] = useState('');
  const [form, setForm] = useState({
    ragsociale: '', indirizzo: '', cap: '', localita: '',
    regioncountry: '', stato: '', telefono: '', email: '',
  });

  useEffect(() => {
    if (show && prospect) {
      loadDetail(prospect.codcliente);
      setCodiceEffettivo('');
    } else {
      setDetail(null);
    }
  }, [show, prospect]);

  const loadDetail = async (codcliente: string) => {
    setLoading(true);
    try {
      const data = await api.get<ProspectDetail>(
        'clienti/cliente/' + config.SITE + '/' + codcliente + '/' + config.NOME_APP
      );
      setDetail(data);
      if (data) {
        setForm({
          ragsociale: data.ragsociale || '',
          indirizzo: data.indirizzo || '',
          cap: data.cap || '',
          localita: data.localita || '',
          regioncountry: data.regioncountry || '',
          stato: data.stato || '',
          telefono: data.telefono || '',
          email: data.email || '',
        });
      }
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      if (codiceEffettivo.trim()) {
        // Codifica come nuovo cliente
        const res = await api.post<{ status: boolean; message?: string }>(
          'clienti/cliente/codeffettivo/save',
          {
            site: config.SITE,
            codazienda: detail.codazienda,
            codtemp: detail.codcliente,
            codeffettivo: codiceEffettivo,
          }
        );
        if (res?.status) {
          showToast('Successo', 'Cliente codificato correttamente', 'success');
        } else {
          showToast('Errore', res?.message || 'Errore durante la codifica', 'danger');
          setSaving(false);
          return;
        }
      } else {
        // Modifica prospect
        const updated = {
          id: detail.id,
          ragsociale: form.ragsociale,
          indirizzo: form.indirizzo,
          cap: form.cap,
          localita: form.localita,
          regioncountry: form.regioncountry,
          stato: form.stato,
          telefono: form.telefono,
          email: form.email,
          camposearch: form.ragsociale,
        };
        await api.post('lmeet/crudapi/' + config.SITE + '/clienti/update', [updated]);
        showToast('Successo', 'Anagrafica modificata correttamente', 'success');
      }
      onHide();
      if (onSaved) onSaved({ codcliente: detail.codcliente, ragsociale: form.ragsociale, indirizzo: form.indirizzo, localita: form.localita, telefono: form.telefono, email: form.email });
    } catch {
      showToast('Errore', 'Errore durante il salvataggio', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const footer = (
    <Button variant="primary" className="flex-fill" onClick={handleSave} disabled={saving}>
      {saving ? <BsSpinner animation="border" size="sm" className="me-1" /> : <FiSave className="me-1" />}
      Salva modifiche
    </Button>
  );

  return (
    <SidePanel show={show} onHide={onHide} title={detail ? '[' + detail.codcliente + '] ' + detail.ragsociale : ''} footer={footer} width={800}>
      {loading && (
        <div className="text-center py-5">
          <BsSpinner animation="border" className="spinner-primary" />
        </div>
      )}

      {!loading && detail && (
        <Row className="g-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Ragione sociale</Form.Label>
              <Form.Control value={form.ragsociale} onChange={e => update('ragsociale', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Codice definitivo SAP</Form.Label>
              <Form.Control value={codiceEffettivo} onChange={e => setCodiceEffettivo(e.target.value)}
                placeholder="Lascia vuoto per modifica" />
            </Form.Group>
          </Col>
          <Col md={9}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Indirizzo</Form.Label>
              <Form.Control value={form.indirizzo} onChange={e => update('indirizzo', e.target.value)} maxLength={150} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>CAP</Form.Label>
              <Form.Control value={form.cap} onChange={e => update('cap', e.target.value)} maxLength={10} />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Città</Form.Label>
              <Form.Control value={form.localita} onChange={e => update('localita', e.target.value)} maxLength={50} />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Regione</Form.Label>
              <Form.Control value={form.regioncountry} onChange={e => update('regioncountry', e.target.value)} maxLength={50} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Stato</Form.Label>
              <Form.Control value={form.stato} onChange={e => update('stato', e.target.value)} maxLength={3} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Telefono</Form.Label>
              <Form.Control value={form.telefono} onChange={e => update('telefono', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: 11 }}>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={e => update('email', e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      )}
    </SidePanel>
  );
}

